// Pure conversion core for the display-timezone feature.
//
// Deliberately importless: `dayjs` is injected by the caller. That keeps webpack
// from having to resolve anything inside a .mjs file, and lets Node load this
// module directly for scripts/verify-timezone.mjs without a build step.

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
// Leading calendar date of an ISO string. Eloquent serialises a date-cast column
// as a full timestamp ("2026-04-20T00:00:00.000000Z"), so anywhere a plain
// YYYY-MM-DD is expected has to cope with that too.
const DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})/;
const TIME_ONLY = /^\d{1,2}:\d{2}(:\d{2})?$/;
const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/;

// `baseline` may be a string or a function. A function lets the caller resolve
// it per call, which is what allows the zone to come from the API at login
// rather than being fixed when this module is first loaded.
export function createTimezoneHelpers(dayjs, { baseline, getDisplayZone }) {
  const isDateOnly = (v) => typeof v === 'string' && DATE_ONLY.test(v.trim());
  const base = () => (typeof baseline === 'function' ? baseline() : baseline);
  const display = () => getDisplayZone() || base();

  // Resolves a value to an absolute instant, interpreting naive strings as
  // wall-clock time in `assumedZone`.
  // dayjs.tz raises RangeError on anything it cannot parse, and these helpers
  // run inside render functions — an exception there blanks the whole component.
  // Invalid input must degrade to an Invalid Date, never throw.
  function safeTz(text, zone) {
    try {
      return dayjs.tz(text, zone);
    } catch (_) {
      return dayjs(null);
    }
  }

  function parseToInstant(value, assumedZone) {
    // Match dayjs's own argument semantics: dayjs(undefined) is "now", while
    // dayjs(null) and dayjs('') are Invalid Date. Folding undefined in with the
    // other two would make every bare $date() call render "Invalid Date".
    if (value === undefined) return dayjs();
    if (value === null || value === '') return dayjs(null);
    if (value instanceof Date) return dayjs(value);
    if (typeof value !== 'string') return dayjs(value);

    const s = value.trim();
    if (DATE_ONLY.test(s)) return dayjs(s);   // floating calendar date, never shifted
    if (TIME_ONLY.test(s)) {                   // bare "09:00" -> today in assumedZone
      const today = dayjs().format('YYYY-MM-DD');
      return safeTz(`${today} ${s.length === 5 ? `${s}:00` : s}`, assumedZone);
    }
    if (HAS_ZONE.test(s)) return dayjs(s);     // already absolute
    return safeTz(s.replace('T', ' '), assumedZone); // naive wall clock
  }

  // Read path: what the user sees.
  function toDisplay(value) {
    if (isDateOnly(value)) return dayjs(value.trim());
    return parseToInstant(value, base()).tz(display());
  }

  // Write path: what gets sent to the API. Must be byte-identical to today's
  // behaviour for a user whose display zone is the baseline.
  function toBaseline(value) {
    if (isDateOnly(value)) return dayjs(value.trim());
    return parseToInstant(value, display()).tz(base());
  }

  // Meetings store date and time as SEPARATE fields, but converting a time
  // across zones can roll the date too. These two must always be used as a
  // pair — converting the fields independently corrupts the date near midnight.
  // Callers pass whatever the API gave them, which for a date-cast column is a
  // full ISO timestamp. Take just the calendar date; anything else is unusable
  // as one half of a date+time pair.
  const calendarDate = (value) => {
    const m = DATE_PREFIX.exec(String(value).trim());
    return m ? m[1] : null;
  };

  const withSeconds = (t) => (String(t).length === 5 ? `${t}:00` : t);

  function splitLocalDateTime(baselineDate, baselineTime) {
    if (!baselineDate || !baselineTime) return { date: baselineDate, time: baselineTime };
    const day = calendarDate(baselineDate);
    if (!day) return { date: baselineDate, time: baselineTime };

    const d = toDisplay(`${day} ${withSeconds(baselineTime)}`);
    if (!d.isValid()) return { date: baselineDate, time: baselineTime };

    return { date: d.format('YYYY-MM-DD'), time: d.format('HH:mm') };
  }

  function joinToBaseline(displayDate, displayTime) {
    if (!displayDate || !displayTime) return { date: displayDate, time: displayTime };
    const day = calendarDate(displayDate);
    if (!day) return { date: displayDate, time: displayTime };

    const d = toBaseline(`${day} ${withSeconds(displayTime)}`);
    if (!d.isValid()) return { date: displayDate, time: displayTime };

    return { date: d.format('YYYY-MM-DD'), time: d.format('HH:mm') };
  }

  return { parseToInstant, toDisplay, toBaseline, isDateOnly, splitLocalDateTime, joinToBaseline };
}
